import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Button, Form, Badge, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { amazonAPI } from '../api/amazonAPI'
import { formatDate, formatDateTime } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import '../styles/selectControl.css'

function AmazonTrafficListScreen() {
    const { hasPermission } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()

    const urlParams = new URLSearchParams(location.state?.returnFilters || location.search)

    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState(() => urlParams.get('account') || '')
    const [error, setError] = useState('')

    const [queries, setQueries] = useState([])
    const [loading, setLoading] = useState(false)
    const autoRefreshRef = useRef(null)

    const [currentPage, setCurrentPage] = useState(() => parseInt(urlParams.get('page')) || 1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [filterStartDate, setFilterStartDate] = useState(() => urlParams.get('filterStart') || '')
    const [filterEndDate, setFilterEndDate] = useState(() => urlParams.get('filterEnd') || '')

    useEffect(() => {
        if (location.state?.returnFilters) {
            navigate(`${location.pathname}${location.state.returnFilters}`, {
                replace: true, state: {}
            })
        }
    }, [])

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                if (data) {
                    const active = data.filter(a => a.is_active)
                    setAccounts(active)
                    if (active.length === 1 && !selectedAccountId) {
                        setSelectedAccountId(active[0].id)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch accounts:', err)
            }
        }
        fetchAccounts()
    }, [])

    useEffect(() => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (filterStartDate) params.set('filterStart', filterStartDate)
        if (filterEndDate) params.set('filterEnd', filterEndDate)
        if (currentPage > 1) params.set('page', currentPage)
        const search = params.toString()
        navigate(`${location.pathname}${search ? '?' + search : ''}`, { replace: true })
    }, [selectedAccountId, filterStartDate, filterEndDate, currentPage])

    const accountOptions = accounts.map(a => ({ value: a.id, label: a.account_name }))

    const getStatusBadge = (st) => {
        const map = { SUBMITTED: 'warning', PROCESSING: 'warning', DONE: 'success', FAILED: 'danger' }
        return <Badge bg={map[st] || 'secondary'}>{st}</Badge>
    }

    const fetchQueries = useCallback(async () => {
        setLoading(true)
        try {
            const params = { page: currentPage, pageSize }
            if (selectedAccountId) params.account_id = selectedAccountId
            if (filterStartDate) params.filter_start_date = filterStartDate
            if (filterEndDate) params.filter_end_date = filterEndDate

            const data = await amazonAPI.traffic.list(params)
            if (data && data.results !== undefined) {
                setQueries(data.results)
                setTotalItems(data.count)
            } else {
                setQueries(data || [])
                setTotalItems((data || []).length)
            }
        } catch (err) {
            console.error('Failed to fetch traffic queries:', err)
        } finally {
            setLoading(false)
        }
    }, [selectedAccountId, currentPage, pageSize, filterStartDate, filterEndDate])

    useEffect(() => { fetchQueries() }, [fetchQueries])

    useEffect(() => {
        const hasActive = queries.some(r => ['SUBMITTED', 'PROCESSING'].includes(r.status))
        if (autoRefreshRef.current) { clearInterval(autoRefreshRef.current); autoRefreshRef.current = null }
        if (hasActive) { autoRefreshRef.current = setInterval(fetchQueries, 30000) }
        return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current) }
    }, [queries, fetchQueries])

    const handleDelete = async (query) => {
        if (!window.confirm('Delete this traffic query and all its data?')) return
        setError('')
        try {
            await amazonAPI.traffic.delete(query.id)
            fetchQueries()
        } catch (err) { setError(formatErrorResponse(err)) }
    }

    const getCurrentFilters = () => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (filterStartDate) params.set('filterStart', filterStartDate)
        if (filterEndDate) params.set('filterEnd', filterEndDate)
        if (currentPage > 1) params.set('page', currentPage)
        const search = params.toString()
        return search ? '?' + search : ''
    }

    const columns = [
        { id: 'account', header: 'Account', cell: (row) => row.seller_account_name || '' },
        { id: 'status', header: 'Status', cell: (row) => getStatusBadge(row.status) },
        { id: 'dataStart', header: 'Data Start', cell: (row) => formatDate(row.data_start_date) },
        { id: 'dataEnd', header: 'Data End', cell: (row) => formatDate(row.data_end_date) },
        { id: 'requestedAt', header: 'Requested At', cell: (row) => formatDateTime(row.requested_at) },
        { id: 'actions', header: 'Actions', cell: (row) => (
            <>
                {row.status === 'DONE' && (
                    <Button variant="primary" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); navigate(`/amazon-traffic/${row.id}`, { state: { returnFilters: getCurrentFilters() } }) }}>
                        Details
                    </Button>
                )}
                {['DONE', 'FAILED'].includes(row.status) && hasPermission('amazon.manage') && (
                    <Button variant="outline-danger" size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>Delete</Button>
                )}
            </>
        )}
    ]

    return (
        <Container fluid className="px-4 py-4">
            <h1 className="mb-3">Traffic Collection</h1>

            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ maxWidth: '900px' }}>
                <div className="d-flex align-items-end gap-3 flex-wrap mb-2">
                    <div style={{ minWidth: '220px', flex: 1 }}>
                        <Form.Label>Amazon Account</Form.Label>
                        <Select
                            value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                            onChange={(opt) => { setSelectedAccountId(opt?.value || ''); setCurrentPage(1) }}
                            options={accountOptions}
                            isClearable
                            placeholder="All Accounts..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>
            </div>

            <hr className="my-4" />

            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Query Requests</h4>
                    <Button variant="outline-secondary" size="sm" onClick={fetchQueries} disabled={loading}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                    </Button>
                </div>

                <div className="d-flex mb-3">
                    <div className="d-flex gap-2 rounded p-2" style={{ background: 'var(--bs-tertiary-bg)' }}>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Requested From</Form.Label>
                            <Form.Control type="date" size="sm" value={filterStartDate}
                                onChange={(e) => { setFilterStartDate(e.target.value); setCurrentPage(1) }}
                                autoComplete="off" spellCheck="false" />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Requested To</Form.Label>
                            <Form.Control type="date" size="sm" value={filterEndDate}
                                onChange={(e) => { setFilterEndDate(e.target.value); setCurrentPage(1) }}
                                autoComplete="off" spellCheck="false" />
                        </Form.Group>
                    </div>
                </div>

                {loading && queries.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : queries.length === 0 ? (
                    <p className="text-muted">No traffic queries found.</p>
                ) : (
                    <>
                        <div className="table-responsive">
                            <ResizableTable columns={columns} data={queries} tableId="amazon_traffic_queries"
                                initialColumnWidths={{ status: 120, dataStart: 140, dataEnd: 140, requestedAt: 180, actions: 220 }}
                                minHeight="auto" striped hover className="table-sm" />
                        </div>
                        <div className="mt-3">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                                onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                            />
                        </div>
                    </>
                )}
            </div>
        </Container>
    )
}

export default AmazonTrafficListScreen
